import { type ReactNode } from 'react';

import cn from 'classnames';

import { Button } from 'ui/atoms/Button/Button';

import styles from './VerificationRecordCard.module.scss';

import type { VerificationRecordLayer } from './VerificationRecordCard.types';

type Props = {
  layer: VerificationRecordLayer;
  className?: string;
};

const SEC_BAR: Record<VerificationRecordLayer, { title: string; meta: string }> = {
  registry: { title: 'Company registry data', meta: 'Companies House · company register' },
  directors: { title: 'Directors & officers', meta: 'Companies House · officers register' },
  ubo: { title: 'Shareholders & UBO', meta: 'Companies House · PSC register' },
  financials: { title: 'Financials', meta: 'Filed accounts · modelled labelled' },
  group: { title: 'Group structure', meta: 'Derived from ownership filings' },
};

const CTA: Record<VerificationRecordLayer, { label: string; href: string }> = {
  registry: { label: 'See the full field list', href: '/data-dictionary' },
  directors: { label: 'Screen directors & officers', href: '/book-a-demo' },
  ubo: { label: 'Resolve beneficial ownership', href: '/book-a-demo' },
  financials: { label: 'Pull company financials', href: '/book-a-demo' },
  group: { label: 'Map the corporate group', href: '/book-a-demo' },
};

type Group = { label: string; rows: [string, ReactNode][] };

const Groups = ({ groups }: { groups: Group[] }) => (
  <div className={styles.groups}>
    {groups.map((group) => (
      <div className={styles.group} key={group.label}>
        <div className={styles.groupLabel}>{group.label}</div>
        {group.rows.map(([k, v]) => (
          <div className={styles.regRow} key={k}>
            <span className={styles.k}>{k}</span>
            <span className={styles.v}>{v}</span>
          </div>
        ))}
      </div>
    ))}
  </div>
);

const RegistryBody = () => (
  <Groups
    groups={[
      {
        label: 'Legal identity',
        rows: [
          ['Registered name', 'ACME TRADING LTD'],
          ['Previous name', 'Acme Trading (UK) Ltd'],
          ['Company number', '08243119'],
          ['Legal form', 'Private limited company'],
          ['Incorporated', '4 October 2012'],
        ],
      },
      {
        label: 'Status & filings',
        rows: [
          ['Registry status', 'Active'],
          ['Jurisdiction', 'England & Wales'],
          ['Confirmation statement', '12 May 2026'],
          ['Accounts next due', '30 September 2026'],
        ],
      },
      {
        label: 'Address & activity',
        rows: [
          ['Registered office', '14 Kingsway, London, WC2B 6LH'],
          ['Trading address', 'Unit 7, Trafford Park, Manchester'],
          ['SIC / activity', '46900 — Non-specialised wholesale'],
        ],
      },
      {
        label: 'Open identifiers',
        rows: [
          ['VAT number', 'GB 342 1198 47'],
          ['LEI', '213800K7VZ9L1B2M4X77'],
          ['Registry key', 'gb/companies-house/08243119'],
        ],
      },
    ]}
  />
);

const DirectorsBody = () => (
  <Groups
    groups={[
      {
        label: 'Officer',
        rows: [
          ['Full name', 'Eleanor J. Whitfield'],
          ['Role', 'Director'],
          ['Status', 'Active'],
        ],
      },
      {
        label: 'Personal',
        rows: [
          ['Date of birth', 'March 1975'],
          ['Nationality', 'British'],
          ['Country of residence', 'United Kingdom'],
          ['Occupation', 'Company director'],
        ],
      },
      {
        label: 'Appointment & screening',
        rows: [
          ['Appointed on', '14 March 2012'],
          ['Resigned on', '—'],
          ['Other directorships', '3 active'],
          ['PEP / sanctions flag', 'No match'],
        ],
      },
    ]}
  />
);

const UBO_SEGMENTS = [
  { name: 'Northgate Holdings Ltd', pct: 58, color: '#3642f5' },
  { name: 'Meridian Capital LP', pct: 27, color: '#6b8fff' },
  { name: 'E. Whitfield', pct: 15, color: '#b7c4f5' },
];

const UboBody = () => (
  <>
    <div className={styles.uboViz}>
      <div className={styles.groupLabel}>Shareholding</div>
      <div className={styles.uboBar}>
        {UBO_SEGMENTS.map((seg) => (
          <span
            key={seg.name}
            className={styles.uboSeg}
            style={{ width: `${seg.pct}%`, background: seg.color }}
            title={`${seg.name} ${seg.pct}%`}
          />
        ))}
      </div>
      <div className={styles.uboLegend}>
        {UBO_SEGMENTS.map((seg) => (
          <span key={seg.name} className={styles.uboLeg}>
            <span className={styles.uboDot} style={{ background: seg.color }} />
            {seg.name} <b>{seg.pct}%</b>
          </span>
        ))}
      </div>
    </div>

    <Groups
      groups={[
        {
          label: 'Shareholder',
          rows: [
            ['Shareholder name', 'Northgate Holdings Ltd'],
            ['Shareholder type', 'Corporate entity'],
            ['Country of registration', 'United Kingdom'],
          ],
        },
        {
          label: 'Holding',
          rows: [
            ['Ownership percentage', '58%'],
            ['Number of shares', '5,800,000'],
            ['Share class', 'Ordinary'],
            ['Nominal value', '£0.01 per share'],
          ],
        },
        {
          label: 'Control & UBO',
          rows: [
            ['UBO flag', 'Yes — beneficial owner'],
            ['PSC nature of control', 'Ownership of more than 50% of shares'],
            ['Date notified', '06 April 2016'],
          ],
        },
      ]}
    />
  </>
);

const FINANCIALS_ROWS: [string, string, string, string][] = [
  ['Reporting period', '12m', '12m', '12m'],
  ['Currency', 'GBP', 'GBP', 'GBP'],
  ['Consolidated accounts', 'Yes', 'Yes', 'Yes'],
  ['Employees', '186', '201', '214'],
  ['Turnover', '£41.0M', '£45.6M', '£48.2M'],
  ['Cost of sales', '£30.2M', '£33.1M', '£34.8M'],
  ['Profit after tax', '£2.1M', '£2.8M', '£3.2M'],
  ['EBITDA', '£4.9M', '£5.6M', '£6.1M'],
  ['Total assets', '£28.4M', '£30.1M', '£31.6M'],
  ['Total liabilities', '£18.9M', '£19.4M', '£19.8M'],
  ['Net assets', '£9.5M', '£10.7M', '£11.8M'],
];

const FinancialsBody = () => (
  <>
    <div className={styles.groupLabel}>Filed accounts</div>
    <div className={styles.tableScroll}>
      <table className={styles.fTable}>
        <thead>
          <tr>
            <th>Metric</th>
            <th className={styles.num}>FY2022</th>
            <th className={styles.num}>FY2023</th>
            <th className={styles.num}>FY2024</th>
          </tr>
        </thead>
        <tbody>
          {FINANCIALS_ROWS.map(([label, y1, y2, y3]) => (
            <tr key={label}>
              <td className={styles.fl}>{label}</td>
              <td className={styles.num}>{y1}</td>
              <td className={styles.num}>{y2}</td>
              <td className={styles.num}>{y3}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <div className={styles.groupLabel}>Modelled</div>
    <div className={styles.regRow}>
      <span className={styles.k}>Estimated revenue</span>
      <span className={styles.v}>
        £52M <span className={styles.modelledTag}>modelled</span>
      </span>
    </div>
  </>
);

const GroupBody = () => (
  <div className={styles.tree}>
    <div className={cn(styles.node, styles.nodeRoot)}>
      Northgate Holdings Ltd <span className={styles.ult}>ULTIMATE PARENT</span>
    </div>
    <div className={styles.branch}>
      <div className={cn(styles.node, styles.nodeSelf)}>
        ACME Trading Ltd <span className={styles.pct}>58%</span> <span className={styles.ult}>THIS ENTITY</span>
      </div>
      <div className={styles.branch}>
        <div className={styles.node}>
          ACME Logistics Ltd <span className={styles.pct}>100% · eff. 58%</span>
        </div>
        <div className={styles.node}>
          ACME Trading GmbH <span className={styles.pct}>100% · eff. 58%</span>
        </div>
        <div className={styles.node}>
          ACME Trading BV <span className={styles.pct}>75% · eff. 43.5%</span>
        </div>
      </div>
    </div>
  </div>
);

const BODIES: Record<VerificationRecordLayer, ReactNode> = {
  registry: <RegistryBody />,
  directors: <DirectorsBody />,
  ubo: <UboBody />,
  financials: <FinancialsBody />,
  group: <GroupBody />,
};

export const VerificationRecordCard = ({ layer, className }: Props) => {
  const secBar = SEC_BAR[layer];
  const cta = CTA[layer];

  return (
    <div className={cn(styles.card, className)}>
      <div className={styles.entHead}>
        <span className={styles.entMeta}>
          <span className={styles.entName}>
            ACME TRADING LTD <span className={styles.samplePill}>Sample</span>
          </span>
          <span className={styles.entSub}>Active</span>
        </span>
      </div>

      <div className={styles.secBar}>
        <span>{secBar.title}</span>
        <span className={styles.secMeta}>{secBar.meta}</span>
      </div>

      <div className={styles.bodyScroll}>{BODIES[layer]}</div>

      <div className={styles.cta}>
        <Button type="text" size="sm" href={cta.href} suffixIcon="arrow-14">
          {cta.label}
        </Button>
      </div>

      <div className={styles.provFoot}>
        <span className={styles.provSrc}>Company number 08243119</span>
        <span className={styles.provLu}>Retrieved 2026-07-17T09:41:02Z</span>
      </div>
    </div>
  );
};
